import { useEffect, useRef, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { FaxHooks, FaxTypes, FaxV1 } from '@frontend/api-fax';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SchemaFaxService } from '@frontend/schema';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useLocation } from '@frontend/weave-router';
import { PopoverMenu, PopoverMenuItem, Text, UsePopoverMenuResponse, useAlert } from '@frontend/design-system';
import { useFaxNavigation } from '../../hooks/use-fax-navigation';
import { TagsBulkSelectPopover } from './tags-bulk-select-popover';

interface Props {
  bulkSelectPopoverMenuProps: UsePopoverMenuResponse<HTMLElement, false, HTMLMenuElement, HTMLButtonElement>;
  selectedFaxes: Record<string, string[]>;
  allFaxes: { id: string; locationId: string; locationPhone?: string }[];
  isSelectAll: boolean;
  resetSelectedFaxes: () => void;
  inboxType: FaxTypes.InboxType;
  tags: Tag[];
}

export const BulkSelectPopover = ({
  bulkSelectPopoverMenuProps,
  selectedFaxes,
  allFaxes,
  isSelectAll,
  resetSelectedFaxes,
  inboxType,
  tags,
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('fax');
  const { invalidateListFaxes, invalidateListArchivedFaxes, removeFromListFaxCache, updateFaxInListFaxCache } =
    FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const invalidateFaxCount = FaxHooks.useInvalidateFaxCount();
  const [selectedSubMenuItem, setSelectedSubMenuItem] = useState<'tags' | undefined>(undefined);
  const { closeThread } = useFaxNavigation();
  const { pathname } = useLocation();
  const openedFaxThreadId = pathname.split('/')[3];

  /*
    We are using a ref here since we need to maintain a master mapping of locationPhone to faxID
    In case we select a fax from a filtered view and then select another received on a different number from another filtered view,
    allFaxes gets updated in that transition and thus during the second selection, the context of the first selection is lost.
    Which would lead to BE receiving only one of the two locationPhones. This approach handles that.
  */
  const locationPhoneMapRef = useRef(new Map<string, string>());
  useEffect(() => {
    allFaxes.forEach((fax) => {
      if (fax.locationPhone) {
        locationPhoneMapRef.current.set(fax.id, fax.locationPhone);
      }
    });
  }, [allFaxes]);

  const getFaxIds = (): Record<string, string[]> => {
    if (isSelectAll) {
      return (allFaxes.filter(({ id, locationId }) => !selectedFaxes[locationId]?.includes(id)) ?? []).reduce<
        Record<string, string[]>
      >((acc, curr) => {
        if (!acc[curr.locationId]) {
          acc[curr.locationId] = [];
        }
        if (!acc[curr.locationId].includes(curr.id)) {
          acc[curr.locationId]?.push(curr.id);
        }
        return acc;
      }, {});
    } else {
      return selectedFaxes;
    }
  };

  const goBack = () => {
    setSelectedSubMenuItem(undefined);
  };

  const handleApply = async (tagIds: string[]) => {
    try {
      const faxIdsMap = getFaxIds();
      await Promise.all(
        Object.entries(faxIdsMap).map(([locationId, faxIds]) => {
          if (faxIds.length === 0) return;
          return SchemaFaxService.TagFaxes({ tagIds, locationId, faxIds });
        })
      );
      alert.success(t('Faxes Tagged'));
      Object.entries(faxIdsMap).forEach(([locationId, faxIds]) =>
        faxIds.forEach((faxId) => updateFaxInListFaxCache({ faxId, locationId }))
      );
      resetSelectedFaxes();
      goBack();
      bulkSelectPopoverMenuProps.close();
    } catch {
      alert.error(t('Failed to tag faxes'));
    }
  };

  const bulkMenuItems = [
    {
      Icon: () => <Icon name='message' size={16} />,
      label: t('Mark Read'),
      onClick: async () => {
        try {
          const faxIdsMap = getFaxIds();
          await Promise.all(
            Object.entries(faxIdsMap).map(([locationId, faxIds]) => {
              if (faxIds.length === 0) return;
              const locationPhones = Array.from(
                new Set(faxIds.map((id) => locationPhoneMapRef.current.get(id)).filter(Boolean))
              );
              return SchemaFaxService.UpdateFaxes({
                hasBeenRead: true,
                locationId,
                faxIds,
                locationPhones: locationPhones as string[],
              });
            })
          );
          alert.success(t('Marked as read'));
          resetSelectedFaxes();
        } catch {
          alert.error(t('Failed to mark as read'));
        }
      },
      key: 'mark-read',
      trackingId: `${FaxPrefixes.List}-bulk-action-mark-read`,
    },
    {
      Icon: () => <Icon name='message-read' size={16} />,
      label: t('Mark Unread'),
      onClick: async () => {
        try {
          const faxIdsMap = getFaxIds();
          await Promise.all(
            Object.entries(faxIdsMap).map(([locationId, faxIds]) => {
              if (faxIds.length === 0) return;
              const locationPhones = Array.from(
                new Set(faxIds.map((id) => locationPhoneMapRef.current.get(id)).filter(Boolean))
              );
              return SchemaFaxService.UpdateFaxes({
                hasBeenRead: false,
                locationId,
                faxIds,
                locationPhones: locationPhones as string[],
              });
            })
          );
          alert.success(t('Marked as unread'));
          resetSelectedFaxes();
        } catch {
          alert.error(t('Failed to mark as unread'));
        }
      },
      key: 'mark-unread',
      trackingId: `${FaxPrefixes.List}-bulk-action-mark-unread`,
    },
    inboxType === FaxTypes.InboxType.ARCHIVED
      ? {
          Icon: () => <Icon name='unarchive' size={16} />,
          label: t('Unarchive'),
          onClick: async () => {
            let isAnySelectedFaxThreadOpen = false;
            try {
              await Promise.all(
                Object.entries(getFaxIds()).map(([locationId, faxIds]) => {
                  if (faxIds.length === 0) return;
                  if (!isAnySelectedFaxThreadOpen && faxIds.includes(openedFaxThreadId))
                    isAnySelectedFaxThreadOpen = true;
                  return SchemaFaxService.UpdateFaxArchiveStatus({ isArchived: false, locationId, faxIds });
                })
              );
              alert.success(t('Faxes Unarchived'));
              resetSelectedFaxes();
              invalidateListFaxes();
              invalidateListArchivedFaxes();
              invalidateFaxCount();
              if (isAnySelectedFaxThreadOpen) closeThread({ type: inboxType });
            } catch {
              alert.error(t('Failed to unarchive faxes'));
            }
          },
          key: 'unarchive',
          trackingId: `${FaxPrefixes.List}-bulk-action-unarchive`,
        }
      : {
          Icon: () => <Icon name='archive' size={16} />,
          label: t('Archive'),
          onClick: async () => {
            let isAnySelectedFaxThreadOpen = false;
            try {
              const faxIdsMap = getFaxIds();
              await Promise.all(
                Object.entries(faxIdsMap).map(([locationId, faxIds]) => {
                  if (faxIds.length === 0) return;
                  if (!isAnySelectedFaxThreadOpen && faxIds.includes(openedFaxThreadId))
                    isAnySelectedFaxThreadOpen = true;
                  return SchemaFaxService.UpdateFaxArchiveStatus({ isArchived: true, locationId, faxIds });
                })
              );
              alert.success(t('Faxes Archived'));
              Object.values(faxIdsMap)
                .flat()
                .forEach((faxId) => removeFromListFaxCache(faxId));
              invalidateListArchivedFaxes();
              invalidateFaxCount();
              resetSelectedFaxes();
              if (isAnySelectedFaxThreadOpen) closeThread({ type: inboxType });
            } catch {
              alert.error(t('Failed to archive faxes'));
            }
          },
          key: 'archive',
          trackingId: `${FaxPrefixes.List}-bulk-action-archive`,
        },
    {
      Icon: () => <Icon name='label' size={16} />,
      label: (
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '75%' }}>
          <Text as='span'>{t('Tag')}</Text>
          <Icon name='caret-right' size={16} />
        </div>
      ),
      onClick: () => {
        setSelectedSubMenuItem('tags');
      },
      key: 'tag',
      trackingId: `${FaxPrefixes.List}-bulk-action-tag`,
      disableCloseOnSelect: true,
    },
    ...(inboxType !== FaxTypes.InboxType.BLOCKED
      ? [
          {
            Icon: () => <Icon name='trash' size={16} />,
            label: t('Delete'),
            onClick: async () => {
              let faxesToDeleteCount = 0;
              let isAnySelectedFaxThreadOpen = false;
              const faxIdsMap = getFaxIds();
              try {
                await Promise.all(
                  Object.entries(faxIdsMap).map(([locationId, faxIds]) => {
                    if (faxIds.length === 0) return;
                    if (!isAnySelectedFaxThreadOpen && faxIds.includes(openedFaxThreadId))
                      isAnySelectedFaxThreadOpen = true;
                    faxesToDeleteCount += faxIds.length;
                    const locationPhones = Array.from(
                      new Set(faxIds.map((id) => locationPhoneMapRef.current.get(id)).filter(Boolean))
                    );
                    return SchemaFaxService.DeleteFaxes({
                      locationId,
                      faxIds,
                      locationPhones: locationPhones as string[],
                    });
                  })
                );
                alert.success(faxesToDeleteCount > 1 ? t('Faxes Deleted') : t('Fax Deleted'));
                Object.values(faxIdsMap)
                  .flat()
                  .forEach((faxId) => removeFromListFaxCache(faxId));
                invalidateFaxCount();
                resetSelectedFaxes();
                if (isAnySelectedFaxThreadOpen) closeThread({ type: inboxType });
              } catch {
                alert.error(faxesToDeleteCount > 1 ? t('Failed to delete faxes') : t('Failed to delete fax'));
              }
            },
            key: 'delete-faxes',
            trackingId: `${FaxPrefixes.List}-bulk-action-delete-faxes`,
          },
        ]
      : []),
  ].filter(Boolean);

  return (
    <>
      <PopoverMenu {...bulkSelectPopoverMenuProps.getMenuProps()}>
        {selectedSubMenuItem === undefined &&
          bulkMenuItems.map(({ label, onClick, key, Icon, trackingId, disableCloseOnSelect = false }, index) => (
            <PopoverMenuItem
              key={key}
              Icon={Icon}
              {...bulkSelectPopoverMenuProps.getItemProps({ index, onClick, disableCloseOnSelect })}
              trackingId={trackingId}
            >
              {label}
            </PopoverMenuItem>
          ))}
        {selectedSubMenuItem === 'tags' && (
          <TagsBulkSelectPopover
            tags={tags}
            popover={bulkSelectPopoverMenuProps}
            goBack={goBack}
            applyTags={handleApply}
          />
        )}
      </PopoverMenu>
    </>
  );
};
